import type { CSSObject } from '@emotion/react';

import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, CalendarDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

const bodyMediumBold = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.bodyMediumBold.fontFamily,
  fontSize: theme.typography.bodyMediumBold.fontSize,
  fontWeight: theme.typography.bodyMediumBold.fontWeight,
  lineHeight: theme.typography.bodyMediumBold.lineHeight,
});

const bodyMedium = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.bodyMedium.fontFamily,
  fontSize: theme.typography.bodyMedium.fontSize,
  fontWeight: theme.typography.bodyMedium.fontWeight,
  lineHeight: theme.typography.bodyMedium.lineHeight,
});

const heading1 = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.heading1.fontFamily,
  fontSize: theme.typography.heading1.fontSize,
  fontWeight: theme.typography.heading1.fontWeight,
  lineHeight: theme.typography.heading1.lineHeight,
});

const heading2 = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.heading2.fontFamily,
  fontSize: theme.typography.heading2.fontSize,
  fontWeight: theme.typography.heading2.fontWeight,
  lineHeight: theme.typography.heading2.lineHeight,
});

const heading3 = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.heading3.fontFamily,
  fontSize: theme.typography.heading3.fontSize,
  fontWeight: theme.typography.heading3.fontWeight,
  lineHeight: theme.typography.heading3.lineHeight,
});

const heading4 = (theme: AlloyLegacyThemeDesignTokens): CSSObject => ({
  fontFamily: theme.typography.heading4.fontFamily,
  fontSize: theme.typography.heading4.fontSize,
  fontWeight: theme.typography.heading4.fontWeight,
  lineHeight: theme.typography.heading4.lineHeight,
});

const subtleBorder = (theme: AlloyLegacyThemeDesignTokens) =>
  theme.tokens.colors.border.subtle.value;

export const getCalendarTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): CalendarDesignTokens => {
  const { tokens, spacing } = theme;
  const { colors } = tokens;
  const borderColor = subtleBorder(theme);

  return {
    container: {
      _: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: borderColor,
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: borderColor,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: borderColor,
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: borderColor,
      },
      compact: {
        true: {
          borderTopWidth: undefined,
          borderTopStyle: undefined,
          borderTopColor: undefined,
          borderRightWidth: undefined,
          borderRightStyle: undefined,
          borderRightColor: undefined,
          borderBottomWidth: undefined,
          borderBottomStyle: undefined,
          borderBottomColor: undefined,
          borderLeftWidth: undefined,
          borderLeftStyle: undefined,
          borderLeftColor: undefined,
        },
        false: {},
      },
    },
    body: {
      _: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gridTemplateRows: 'auto',
        gridAutoRows: '1fr',
        height: '100%',
      },
    },
    weekLabel: {
      _: {
        ...bodyMediumBold(theme),
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: px(spacing.XS),
        paddingRight: px(spacing.XS),
        paddingBottom: px(spacing.XS),
        paddingLeft: px(spacing.XS),
        justifyContent: 'center',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: borderColor,
        '&:not(:first-child)': {
          borderLeftWidth: '1px',
          borderLeftStyle: 'solid',
          borderLeftColor: borderColor,
        },
      },
      compact: {
        true: {
          borderTopWidth: undefined,
          borderTopStyle: undefined,
          borderTopColor: undefined,
          '&:not(:first-child)': {
            borderLeftWidth: undefined,
            borderLeftStyle: undefined,
            borderLeftColor: undefined,
          },
        },
        false: {},
      },
    },
    header: {
      _: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: px(spacing.XS),
        paddingRight: px(spacing.M),
        paddingBottom: px(spacing.XS),
        paddingLeft: px(spacing.M),
        color: colors.text.primary.value,
      },
      title: {
        ...heading2(theme),
        color: colors.text.primary.value,
        display: 'inline-flex',
        alignItems: 'center',
      },
      titleGroup: {
        display: 'flex',
        alignItems: 'center',
        minWidth: '0',
      },
      leftSection: {
        display: 'flex',
        alignItems: 'center',
      },
      rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: px(spacing.XS),
      },
      spacer: {
        flex: '1',
      },
      compact: {
        _: {
          display: 'flex',
          alignItems: 'center',
          paddingTop: px(spacing.XXS),
          paddingRight: px(spacing.XXS),
          paddingBottom: px(spacing.XXS),
          paddingLeft: px(spacing.XXS),
          justifyContent: 'space-between',
          color: colors.text.primary.value,
        },
        title: {
          ...heading3(theme),
          color: colors.text.primary.value,
        },
      },
    },
    navButton: {
      _: {
        ...heading1(theme),
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.text.inverse.value,
        cursor: 'pointer',
        border: 'none',
        paddingTop: px(spacing.XS),
        paddingRight: px(spacing.XS),
        paddingBottom: px(spacing.XS),
        paddingLeft: px(spacing.XS),
      },
    },
    compactDayCell: {
      _: {
        ...bodyMediumBold(theme),
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: px(spacing.XXL),
        paddingTop: px(spacing.XS),
        paddingRight: px(spacing.XS),
        paddingBottom: px(spacing.XS),
        paddingLeft: px(spacing.XS),
        borderRadius: px(spacing.XXS),
        border: 'none',
        cursor: 'default',
        backgroundColor: 'unset',
        color: colors.text.inactive.value,
        '&:hover:not([data-active="true"])': {
          backgroundColor: colors.background.neutral.hover.value,
        },
      },
      active: {
        true: {
          backgroundColor: colors.background.active.value,
          color: colors.text.inverse.value,
        },
        false: {},
      },
      thisMonth: {
        true: {
          color: colors.text.primary.value,
        },
        false: {},
      },
      compoundVariants: [
        {
          active: true,
          thisMonth: true,
          styles: {
            color: colors.text.inverse.value,
          },
        },
        {
          active: true,
          thisMonth: false,
          styles: {
            color: colors.text.inverse.value,
          },
        },
      ],
    },
    monthDayCell: {
      _: {
        ...bodyMediumBold(theme),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        userSelect: 'none',
        border: 'none',
        paddingTop: px(spacing.XS),
        paddingRight: px(spacing.XS),
        paddingBottom: px(spacing.XS),
        paddingLeft: px(spacing.XS),
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: borderColor,
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: colors.background.selected.subtler.value,
        },
      },
      selected: {
        true: {
          backgroundColor: colors.background.selected.subtler.value,
        },
        false: {},
      },
      firstDay: {
        true: {
          borderLeftWidth: undefined,
          borderLeftStyle: undefined,
          borderLeftColor: undefined,
        },
        false: {
          borderLeftWidth: '1px',
          borderLeftStyle: 'solid',
          borderLeftColor: borderColor,
        },
      },
    },
    weekDayCell: {
      _: {
        ...bodyMediumBold(theme),
        flex: '1',
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: px(spacing.XS),
        userSelect: 'none',
        paddingTop: px(spacing.S),
        paddingRight: px(spacing.S),
        paddingBottom: px(spacing.S),
        paddingLeft: px(spacing.S),
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: borderColor,
        backgroundColor: 'inherit',
        '&:not(:last-child)': {
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: borderColor,
        },
        '&:hover': {
          backgroundColor: colors.background.selected.subtler.value,
        },
      },
      selected: {
        true: {
          backgroundColor: colors.background.selected.subtler.value,
        },
        false: {},
      },
    },
    dayNumber: {
      _: {
        ...bodyMediumBold(theme),
        paddingTop: px(spacing.XXS),
        paddingRight: px(spacing.XXS),
        paddingBottom: px(spacing.XXS),
        paddingLeft: px(spacing.XXS),
        width: px(spacing.L),
        height: px(spacing.L),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: 'unset',
        color: colors.text.inactive.value,
      },
      active: {
        true: {
          backgroundColor: colors.background.active.value,
          color: colors.text.inverse.value,
        },
        false: {},
      },
      thisMonth: {
        true: {
          color: colors.text.primary.value,
        },
        false: {},
      },
    },
    eventDot: {
      _: {
        position: 'absolute',
        top: px(spacing.XL + spacing.XXS),
        borderRadius: '50%',
        height: px(spacing.XXS),
        width: px(spacing.XXS),
        backgroundColor: colors.icon.primary.value,
      },
      active: {
        true: {
          backgroundColor: colors.icon.inverse.value,
        },
        false: {},
      },
    },
    countBadge: {
      _: {
        ...bodyMedium(theme),
        color: colors.text.primary.value,
        backgroundColor: colors.background.selected.subtle.value,
        borderRadius: px(spacing.XXS),
        minWidth: px(spacing.L + spacing.XXS),
        minHeight: px(spacing.L + spacing.XXS),
        paddingTop: '0',
        paddingRight: px(spacing.XXS),
        paddingBottom: '0',
        paddingLeft: px(spacing.XXS),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      hover: {
        backgroundColor: colors.background.hover.value,
      },
    },
    itemBadge: {
      _: {
        ...bodyMedium(theme),
        position: 'relative',
        color: colors.text.primary.value,
        backgroundColor: colors.background.selected.subtler.value,
        paddingTop: px(spacing.XXS),
        paddingRight: px(spacing.M),
        paddingBottom: px(spacing.XXS),
        paddingLeft: px(spacing.M),
        borderRadius: px(spacing.XXS),
        marginBottom: px(spacing.XXS),
        width: '100%',
        overflow: 'clip',
        whiteSpace: 'nowrap',
      },
      marker: {
        content: "''",
        position: 'absolute',
        top: '20%',
        left: px(spacing.XXS),
        width: px(spacing.XXS),
        borderRadius: px(spacing.XXS),
        height: '60%',
        backgroundColor: colors.background.pending.value,
      },
      colored: {
        color: colors.text.primary.value,
      },
    },
    flyout: {
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: px(spacing.S),
        minWidth: '200px',
      },
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: borderColor,
        paddingBottom: px(spacing.XS),
      },
      headerLabel: {
        ...bodyMediumBold(theme),
        color: colors.text.primary.value,
      },
      items: {
        display: 'flex',
        flexDirection: 'column',
        gap: px(spacing.XS),
      },
    },
    monthDayItems: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dayNumberText: bodyMediumBold(theme),
    weekDayHeaderText: {
      ...bodyMediumBold(theme),
      display: 'inline-flex',
      alignItems: 'center',
      gap: px(spacing.XS),
    },
    weekView: {
      display: 'flex',
      flexDirection: 'row',
      flex: '1',
    },
    monthView: {
      flex: '1',
    },
    calendarLabel: {
      ...heading4(theme),
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      textAlign: 'center',
      userSelect: 'none',
      paddingTop: px(spacing.XXS),
      paddingRight: px(spacing.XXS),
      paddingBottom: px(spacing.XXS),
      paddingLeft: px(spacing.XXS),
    },
    todayButtonMargin: {
      marginRight: px(spacing.XXS),
    },
    loadingMargin: {
      marginLeft: px(spacing.S),
      display: 'flex',
      alignItems: 'center',
      flexShrink: '0',
    },
    navButtonCompactPadding: {
      paddingTop: px(spacing.XXS),
      paddingRight: px(spacing.XXS),
      paddingBottom: px(spacing.XXS),
      paddingLeft: px(spacing.XXS),
    },
    navButtonPrevSpacing: {
      paddingRight: px(spacing.XXS),
    },
    navButtonNextSpacing: {
      paddingLeft: px(spacing.XXS),
    },
  };
};

const isLightSurface = (surface: string | undefined) =>
  surface === '#ffffff' || surface === '#fff';

type BoxShadowParts = {
  x?: string;
  y?: string;
  blur?: string;
  color?: string;
};

const composeBoxShadow = ({
  x,
  y,
  blur,
  color,
}: BoxShadowParts): string | undefined => {
  if (!x || !y || !blur || !color) {
    return undefined;
  }

  return `${x} ${y} ${blur} ${color}`;
};

const getWeaveFlyoutShell = (
  tokens: WeaveTokens,
  textPrimary: string | undefined,
  bgSurface: string | undefined,
): CalendarDesignTokens['flyout']['shell'] => {
  if (isLightSurface(bgSurface)) {
    return undefined;
  }

  const popover = tokens?.component?.popover;
  const popoverBackground = popover?.['background-color'];
  const shadowX = popover?.['box-shadow-x'];
  const shadowY = popover?.['box-shadow-y'];
  const shadowBlur = popover?.['box-shadow-blur'];
  const shadowColor = popover?.['box-shadow-color'];

  return {
    backgroundColor: popoverBackground,
    color: textPrimary,
    borderRadius: popover?.['border-radius'],
    boxShadow: composeBoxShadow({
      x: shadowX,
      y: shadowY,
      blur: shadowBlur,
      color: shadowColor,
    }),
    arrow: {
      backgroundColor: popoverBackground,
    },
  };
};

export const getCalendarTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): CalendarDesignTokens => {
  const dp = tokens?.component?.datepicker;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;

  const cellSeparatorColor = dp?.cell?.['border-color']?.default;
  const textPrimary = semantic?.['text-color']?.medium?.default;
  const textOnColoredBackground =
    semantic?.['text-color']?.['on-status']?.light;
  const textSubtle = semantic?.['text-color']?.light;
  const iconMedium = semantic?.['icon-color']?.medium;
  const bgSurface = semantic?.['background-color']?.surface?.[100];
  const bgHover = dp?.cell?.['background-color']?.hover;
  const bgSelected = dp?.cell?.['background-color']?.selected;
  const bgSelectedSubtle = dp?.range?.cell?.['background-color']?.default;
  const bgSelectedSubtleHover = dp?.range?.cell?.['background-color']?.hover;
  const dividerColor = semantic?.['border-color']?.divider?.light;
  const borderRadius4 = generic?.['border-radius']?.['4'];
  const spacing4 = generic?.spacing?.['4'];
  const spacing8 = generic?.spacing?.['8'];
  const spacing12 = generic?.spacing?.['12'];
  const spacing16 = generic?.spacing?.['16'];
  const spacing24 = generic?.spacing?.['24'];
  const spacing48 = generic?.spacing?.['48'];

  const weekHeaderFontSize = dp?.['week-header']?.['font-size'];
  const weekHeaderFontWeight = dp?.['week-header']?.['font-weight'];
  const weekHeaderLineHeight = dp?.['week-header']?.['line-height'];
  const cellFontSize = dp?.cell?.day?.['font-size'];
  const cellFontWeight = dp?.cell?.day?.['font-weight'];
  const cellLineHeight = dp?.cell?.day?.['line-height'];

  const bodyMediumBoldBlock: CSSObject = {
    fontFamily,
    fontSize: cellFontSize,
    fontWeight: cellFontWeight,
    lineHeight: cellLineHeight,
  };

  const weekHeaderTypography: CSSObject = {
    fontFamily,
    fontSize: weekHeaderFontSize,
    fontWeight: weekHeaderFontWeight,
    lineHeight: weekHeaderLineHeight,
  };

  return {
    container: {
      _: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: dividerColor,
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: dividerColor,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: dividerColor,
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: dividerColor,
      },
      compact: {
        true: {
          borderTopWidth: undefined,
          borderTopStyle: undefined,
          borderTopColor: undefined,
          borderRightWidth: undefined,
          borderRightStyle: undefined,
          borderRightColor: undefined,
          borderBottomWidth: undefined,
          borderBottomStyle: undefined,
          borderBottomColor: undefined,
          borderLeftWidth: undefined,
          borderLeftStyle: undefined,
          borderLeftColor: undefined,
        },
        false: {},
      },
    },
    body: {
      _: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gridTemplateRows: 'auto',
        gridAutoRows: '1fr',
        height: '100%',
      },
      weeks: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: bgSurface,
        flex: '1',
        width: '100%',
        minHeight: '0',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: cellSeparatorColor,
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: cellSeparatorColor,
      },
      weekHeader: {
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
        paddingBottom: dp?.['week-header']?.['padding-bottom'],
        fontFamily,
        fontSize: weekHeaderFontSize,
        fontWeight: weekHeaderFontWeight,
        lineHeight: weekHeaderLineHeight,
        color: dp?.['week-header']?.['text-color'],
      },
      weekRow: {
        display: 'flex',
        margin: '0',
        width: '100%',
        flex: '1',
        minHeight: '0',
      },
      cellBorder: {
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: cellSeparatorColor,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: cellSeparatorColor,
      },
      cellWrapper: {
        margin: '0',
        flex: '1',
        minWidth: '0',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      },
    },
    weekLabel: {
      _: {
        ...weekHeaderTypography,
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: spacing8,
        paddingRight: spacing8,
        paddingBottom: dp?.['week-header']?.['padding-bottom'],
        paddingLeft: spacing8,
        justifyContent: 'center',
        color: dp?.['week-header']?.['text-color'],
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: cellSeparatorColor,
        '&:not(:first-child)': {
          borderLeftWidth: '1px',
          borderLeftStyle: 'solid',
          borderLeftColor: cellSeparatorColor,
        },
      },
      compact: {
        true: {
          borderTopWidth: undefined,
          borderTopStyle: undefined,
          borderTopColor: undefined,
          '&:not(:first-child)': {
            borderLeftWidth: undefined,
            borderLeftStyle: undefined,
            borderLeftColor: undefined,
          },
        },
        false: {},
      },
      gridHeader: {
        borderTopWidth: undefined,
        borderTopStyle: undefined,
        borderTopColor: undefined,
        borderLeftWidth: undefined,
        borderLeftStyle: undefined,
        borderLeftColor: undefined,
        width: '100%',
        justifyContent: 'center',
        '&:not(:first-child)': {
          borderLeftWidth: undefined,
          borderLeftStyle: undefined,
          borderLeftColor: undefined,
        },
      },
    },
    header: {
      _: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: spacing8,
        paddingRight: dp?.header?.['padding-right'],
        paddingBottom: spacing8,
        paddingLeft: dp?.header?.['padding-left'],
        color: textPrimary,
      },
      title: {
        fontFamily,
        fontSize: dp?.header?.['font-size'],
        fontWeight: dp?.header?.['font-weight'],
        lineHeight: dp?.header?.['line-height'],
        color: textPrimary,
        display: 'inline-flex',
        alignItems: 'center',
      },
      titleGroup: {
        display: 'flex',
        alignItems: 'center',
        minWidth: '0',
      },
      leftSection: {
        display: 'flex',
        alignItems: 'center',
      },
      rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing8,
      },
      spacer: {
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        minWidth: '0',
        '& > *': {
          flex: '1',
          minWidth: '0',
        },
      },
      compact: {
        _: {
          display: 'flex',
          alignItems: 'center',
          paddingTop: spacing4,
          paddingRight: spacing4,
          paddingBottom: spacing4,
          paddingLeft: spacing4,
          justifyContent: 'space-between',
          color: textPrimary,
        },
        title: {
          fontFamily,
          fontSize: dp?.header?.['font-size'],
          fontWeight: dp?.header?.['font-weight'],
          lineHeight: dp?.header?.['line-height'],
          color: textPrimary,
        },
      },
    },
    navButton: {
      _: {
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        border: 'none',
        color: iconMedium,
        paddingTop: spacing8,
        paddingRight: spacing8,
        paddingBottom: spacing8,
        paddingLeft: spacing8,
        '& svg, & svg path': {
          color: iconMedium,
          fill: iconMedium,
        },
      },
    },
    compactDayCell: {
      _: {
        ...bodyMediumBoldBlock,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        flex: '1',
        minHeight: spacing48,
        paddingTop: spacing8,
        paddingRight: spacing8,
        paddingBottom: spacing8,
        paddingLeft: spacing8,
        borderRadius: '0',
        border: 'none',
        cursor: 'default',
        backgroundColor: 'transparent',
        color: textSubtle,
        boxSizing: 'border-box',
        '&:hover:not([data-active="true"])': {
          backgroundColor: bgHover,
        },
      },
      active: {
        true: {
          backgroundColor: bgSelected,
          color: dp?.cell?.['text-color']?.selected,
        },
        false: {},
      },
      thisMonth: {
        true: {
          color: textPrimary,
        },
        false: {},
      },
      compoundVariants: [
        {
          active: true,
          thisMonth: true,
          styles: {
            color: dp?.cell?.['text-color']?.selected,
          },
        },
        {
          active: true,
          thisMonth: false,
          styles: {
            color: dp?.cell?.['text-color']?.selected,
          },
        },
      ],
    },
    monthDayCell: {
      _: {
        ...bodyMediumBoldBlock,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        userSelect: 'none',
        border: 'none',
        width: '100%',
        height: '100%',
        flex: '1',
        minHeight: '0',
        paddingTop: spacing8,
        paddingRight: spacing8,
        paddingBottom: spacing8,
        paddingLeft: spacing8,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: bgSelectedSubtleHover,
        },
      },
      selected: {
        true: {
          backgroundColor: bgSelectedSubtle,
        },
        false: {},
      },
      firstDay: {
        true: {},
        false: {},
      },
    },
    weekDayCell: {
      _: {
        ...bodyMediumBoldBlock,
        flex: '1',
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: spacing8,
        userSelect: 'none',
        color: textPrimary,
        paddingTop: spacing12,
        paddingRight: spacing12,
        paddingBottom: spacing12,
        paddingLeft: spacing12,
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: cellSeparatorColor,
        backgroundColor: 'transparent',
        '&:not(:last-child)': {
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: cellSeparatorColor,
        },
        '&:hover': {
          backgroundColor: bgSelectedSubtleHover,
        },
      },
      selected: {
        true: {
          backgroundColor: bgSelectedSubtle,
        },
        false: {},
      },
    },
    dayNumber: {
      _: {
        ...bodyMediumBoldBlock,
        paddingTop: spacing4,
        paddingRight: spacing4,
        paddingBottom: spacing4,
        paddingLeft: spacing4,
        width: spacing24,
        height: spacing24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        color: textSubtle,
      },
      active: {
        true: {
          backgroundColor: bgSelected,
          color: dp?.cell?.['text-color']?.selected,
        },
        false: {},
      },
      thisMonth: {
        true: {
          color: textPrimary,
        },
        false: {},
      },
    },
    eventDot: {
      _: {
        position: 'absolute',
        bottom: spacing12,
        borderRadius: '50%',
        height: spacing4,
        width: spacing4,
        backgroundColor:
          dp?.cell?.['text-color']?.notable !== undefined
            ? dp.cell['text-color'].notable
            : iconMedium,
      },
      active: {
        true: {
          backgroundColor: dp?.cell?.['text-color']?.selected,
        },
        false: {},
      },
    },
    countBadge: {
      _: {
        ...bodyMediumBoldBlock,
        color: textPrimary,
        backgroundColor: bgSelectedSubtle,
        borderRadius: borderRadius4,
        minWidth: spacing24,
        minHeight: spacing24,
        paddingTop: '0',
        paddingRight: spacing4,
        paddingBottom: '0',
        paddingLeft: spacing4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      hover: {
        backgroundColor: bgHover,
      },
    },
    itemBadge: {
      _: {
        ...bodyMediumBoldBlock,
        position: 'relative',
        color: textPrimary,
        backgroundColor: bgSelectedSubtle,
        paddingTop: spacing4,
        paddingRight: spacing16,
        paddingBottom: spacing4,
        paddingLeft: spacing16,
        borderRadius: borderRadius4,
        marginBottom: spacing4,
        width: '100%',
        overflow: 'clip',
        whiteSpace: 'nowrap',
      },
      marker: {
        content: "''",
        position: 'absolute',
        top: '20%',
        left: spacing4,
        width: spacing4,
        borderRadius: spacing4,
        height: '60%',
        backgroundColor: dp?.cell?.['text-color']?.notable,
      },
      colored: {
        color: textOnColoredBackground,
      },
    },
    flyout: {
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing12,
        minWidth: '200px',
      },
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: dividerColor,
        paddingBottom: spacing8,
      },
      headerLabel: {
        ...bodyMediumBoldBlock,
        color: textPrimary,
      },
      items: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing8,
      },
      shell: getWeaveFlyoutShell(tokens, textPrimary, bgSurface),
    },
    monthDayItems: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dayNumberText: bodyMediumBoldBlock,
    weekDayHeaderText: {
      ...bodyMediumBoldBlock,
      color: textPrimary,
      display: 'inline-flex',
      alignItems: 'center',
      gap: spacing8,
    },
    weekView: {
      display: 'flex',
      flexDirection: 'row',
      flex: '1',
    },
    monthView: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: '0',
    },
    calendarLabel: {
      ...weekHeaderTypography,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      textAlign: 'center',
      userSelect: 'none',
      paddingTop: spacing4,
      paddingRight: spacing4,
      paddingBottom: spacing4,
      paddingLeft: spacing4,
    },
    todayButtonMargin: {
      marginRight: spacing4,
    },
    loadingMargin: {
      marginLeft: spacing16,
      display: 'flex',
      alignItems: 'center',
      flexShrink: '0',
    },
    navButtonCompactPadding: {
      paddingTop: spacing4,
      paddingRight: spacing4,
      paddingBottom: spacing4,
      paddingLeft: spacing4,
    },
    navButtonPrevSpacing: {
      paddingRight: spacing4,
    },
    navButtonNextSpacing: {
      paddingLeft: spacing4,
    },
  };
};
